import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeftIcon as ArrowLeft,
  CircleNotchIcon as Spinner,
  CloudIcon as Cloud,
  CodeIcon as Code,
  CpuIcon as Cpu,
  DatabaseIcon as Database,
  FileTextIcon as FileText,
  UploadSimpleIcon as Upload,
  DownloadSimpleIcon as Download,
  LinkIcon as LinkIcon,
  CheckCircleIcon as CheckCircle,
  WarningOctagonIcon as WarningOctagon,
} from "@phosphor-icons/react";
import {
  getSapOverview,
  getSapPtfHealth,
  importSapExtract,
  importSapBulkZip,
  listSapTables,
  sapTemplateUrl,
} from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";
import AbapObjectsTab from "@/components/sapecc/AbapObjectsTab";

const TABS = [
  { id: "overview", label: "Overview", Icon: Cloud },
  { id: "components", label: "Components", Icon: Cloud },
  { id: "platform", label: "Platform", Icon: Cpu },
  { id: "tables", label: "Tables", Icon: Database },
  { id: "abap", label: "ABAP Code", Icon: Code },
  { id: "import", label: "Import", Icon: Upload },
];

const TEMPLATE_KEYS = [
  { key: "components", label: "Components (SVERS)", desc: "SAP_BASIS / SAP_APPL / SAP_ABA + EhP + SP level" },
  { key: "platform",   label: "Platform inventory", desc: "IBM i OS, SAP kernel, Db2 group PTF, JVM" },
  { key: "sap_tables", label: "Db2 / SAP tables",   desc: "DD02L join: SAP table → IBM i PF correlation" },
  { key: "abap_tadir", label: "ABAP objects (TADIR)", desc: "Z*/Y* + standard ABAP repository directory" },
  { key: "abap_reposrc", label: "ABAP source (REPOSRC)", desc: "Per-program LOC + last-changed enrichment" },
  { key: "abap_enhancements", label: "SMOD / CMOD enhancements", desc: "Customer-implemented function/screen/menu/field exits + BAdIs" },
];


export default function WorkspaceSapEcc() {
  const { id } = useParams();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSapOverview(id);
      setOverview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="sap-ecc-page">
      <Link
        to={`/workspaces/${id}`}
        className="text-sm text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] flex items-center gap-1 mb-4"
        data-testid="sap-ecc-back"
      >
        <ArrowLeft size={14} weight="bold" /> Back to workspace
      </Link>
      <div className="mb-6 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-1">
            <Cloud size={11} weight="bold" /> SAP ECC on IBM i · Iter 27 + 28
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">SAP ECC Transition Analysis</h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1 max-w-3xl">
            Hybrid discovery for SAP ECC hosted on IBM i / Db2 for i. Captures the
            SAP stack, the platform underneath, the Db2-for-i × SAP-table correlation,
            and the ABAP custom-code footprint — alongside (not replacing) the existing IBM i metadata.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--ml-border)] mb-6 overflow-x-auto" data-testid="sap-ecc-tabs">
        {TABS.map(({ id: tid, label, Icon }) => (
          <button
            key={tid}
            type="button"
            onClick={() => setTab(tid)}
            data-testid={`sap-ecc-tab-${tid}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              tab === tid
                ? "border-[var(--ml-primary)] text-[var(--ml-primary)]"
                : "border-transparent text-[var(--ml-text-secondary)] hover:text-[var(--ml-text-primary)]"
            }`}
          >
            <Icon size={13} weight="bold" /> {label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="ml-card p-10 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="sap-ecc-loading">
          <Spinner size={16} className="animate-spin inline mr-2" />
          Loading SAP-ECC inventory…
        </div>
      )}

      {!loading && overview && (
        <>
          {tab === "overview" && <OverviewTab overview={overview} workspaceId={id} onImport={() => setTab("import")} />}
          {tab === "components" && <ComponentsTab components={overview.components} />}
          {tab === "platform" && <PlatformTab platform={overview.platform} />}
          {tab === "tables" && <TablesTab workspaceId={id} />}
          {tab === "abap" && <AbapObjectsTab workspaceId={id} />}
          {tab === "import" && <ImportTab workspaceId={id} onReload={load} />}
        </>
      )}
    </div>
  );
}

function PtfHealthBar({ workspaceId }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getSapPtfHealth(workspaceId)
      .then((d) => { if (!cancelled) setHealth(d); })
      .catch(() => { if (!cancelled) setHealth({ status: "error" }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workspaceId]);

  if (loading || !health) return null;

  // Per-status visual treatment. Colours pulled from the IBM Carbon palette
  // used elsewhere in the app so the bar feels native.
  const STYLES = {
    current:           { bg: "#defbe6", fg: "#0e6027", border: "#a7f0ba", Icon: CheckCircle },
    behind:            { bg: "#fff8e1", fg: "#8a6c00", border: "#f1c21b", Icon: WarningOctagon },
    ahead:             { bg: "#edf5ff", fg: "#0043ce", border: "#a6c8ff", Icon: WarningOctagon },
    unknown_sf:        { bg: "#f4f4f4", fg: "#525252", border: "#e0e0e0", Icon: FileText },
    unparseable_level: { bg: "#f4f4f4", fg: "#525252", border: "#e0e0e0", Icon: FileText },
    no_inventory:     { bg: "#edf5ff", fg: "#0043ce", border: "#a6c8ff", Icon: Upload },
    error:             { bg: "#fff1f1", fg: "#a2191f", border: "#ffd7d9", Icon: WarningOctagon },
  };
  const s = STYLES[health.status] || STYLES.unknown_sf;
  const Icon = s.Icon;

  return (
    <div
      data-testid="sap-ptf-health-bar"
      data-status={health.status}
      className="ml-card flex items-start gap-3 px-4 py-3 border-l-4"
      style={{ background: s.bg, color: s.fg, borderLeftColor: s.fg, borderColor: s.border }}
    >
      <Icon size={18} weight="bold" className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="ml-mono text-[10px] uppercase tracking-widest mb-0.5">
          Db2 for i Group PTF · SAP Note {health.sap_note || "1556808"}
        </div>
        <div className="text-sm font-medium" data-testid="sap-ptf-health-message">
          {health.message}
        </div>
        {(health.imported_level != null || health.current_level != null) && (
          <div className="ml-mono text-[11px] mt-1 opacity-80" data-testid="sap-ptf-health-levels">
            {health.sf_id && <>{health.sf_id} · </>}
            {health.imported_level != null && <>Imported L{health.imported_level} · </>}
            {health.current_level != null && <>Catalog L{health.current_level}</>}
            {health.current_as_of && <> (verified {health.current_as_of})</>}
            {health.catalog_age_days != null && health.catalog_age_days > 90 && (
              <> · <strong>Catalog is {health.catalog_age_days} days old</strong></>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function StatCard({ label, value, Icon, color }) {
  return (
    <div className="ml-card p-4 flex items-center gap-3" data-testid={`sap-stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div
        className="w-9 h-9 flex items-center justify-center shrink-0"
        style={{ background: `${color}14`, color }}
      >
        <Icon size={18} weight="bold" />
      </div>
      <div>
        <div className="text-2xl font-semibold leading-none">{value}</div>
        <div className="ml-mono text-[9px] uppercase tracking-widest text-[var(--ml-text-secondary)] mt-1">{label}</div>
      </div>
    </div>
  );
}

function OverviewTab({ overview, onImport, workspaceId }) {
  const { stats, components, platform } = overview;
  const basis = components.find((c) => c.component === "SAP_BASIS");
  const appl = components.find((c) => c.component === "SAP_APPL");
  const os = platform.find((p) => p.kind === "os");
  const kernel = platform.find((p) => p.kind === "kernel");
  const isEmpty = stats.components === 0 && stats.platform_items === 0 && stats.sap_tables === 0 && (stats.abap_objects || 0) === 0;

  if (isEmpty) {
    return (
      <div className="ml-card p-10 text-center" data-testid="sap-ecc-empty">
        <Cloud size={40} weight="duotone" className="text-[var(--ml-text-secondary)] inline mb-3" />
        <h3 className="text-lg font-semibold mb-1">No SAP ECC data ingested yet</h3>
        <p className="text-sm text-[var(--ml-text-secondary)] mb-4 max-w-md mx-auto">
          Upload your SAP-side extracts (components, platform inventory, Db2/SAP table list, ABAP custom code)
          to populate this module. Existing IBM i metadata stays intact.
        </p>
        <button
          onClick={onImport}
          className="ml-btn-primary"
          data-testid="sap-ecc-empty-go-import"
        >
          <Upload size={14} weight="bold" /> Start importing
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sap-ecc-overview">
      <PtfHealthBar workspaceId={workspaceId} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Components" value={stats.components} Icon={Cloud} color="#0f62fe" />
        <StatCard label="Platform items" value={stats.platform_items} Icon={Cpu} color="#6929c4" />
        <StatCard label="SAP tables" value={stats.sap_tables} Icon={Database} color="#0e6027" />
        <StatCard label="Linked to IBM i" value={stats.sap_tables_linked_to_ibmi} Icon={LinkIcon} color="#0f62fe" />
      </div>

      {(stats.abap_objects || 0) > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="sap-overview-abap-strip">
          <StatCard label="ABAP objects" value={stats.abap_objects} Icon={Code} color="#0f62fe" />
          <StatCard label="Custom (Z/Y)" value={stats.abap_custom_objects} Icon={Code} color="#0e6027" />
          <StatCard label="Modified standard" value={stats.abap_modified_standard} Icon={WarningOctagon} color="#a2191f" />
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <div className="ml-card p-5" data-testid="sap-overview-stack">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-3 flex items-center gap-1.5">
            <Cloud size={11} weight="bold" /> SAP application stack
          </div>
          {basis && (
            <div className="mb-2">
              <div className="font-semibold ml-mono">SAP_BASIS {basis.release}</div>
              <div className="text-xs text-[var(--ml-text-secondary)]">{basis.sp_level || "—"}{basis.patch_level ? ` · Patch ${basis.patch_level}` : ""}</div>
            </div>
          )}
          {appl && (
            <div>
              <div className="font-semibold ml-mono">SAP_APPL {appl.release} {appl.ehp || ""}</div>
              <div className="text-xs text-[var(--ml-text-secondary)]">{appl.sp_level || "—"}{appl.patch_level ? ` · Patch ${appl.patch_level}` : ""}</div>
            </div>
          )}
          {!basis && !appl && <div className="text-sm text-[var(--ml-text-secondary)]">No SAP_BASIS or SAP_APPL component recorded yet.</div>}
        </div>

        <div className="ml-card p-5" data-testid="sap-overview-platform">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-3 flex items-center gap-1.5">
            <Cpu size={11} weight="bold" /> Platform stack
          </div>
          {os && (
            <div className="mb-2">
              <div className="font-semibold ml-mono">{os.name} {os.version}</div>
              <div className="text-xs text-[var(--ml-text-secondary)]">{os.patch_level || "—"}{os.notes ? ` · ${os.notes}` : ""}</div>
            </div>
          )}
          {kernel && (
            <div>
              <div className="font-semibold ml-mono">{kernel.name} {kernel.version}</div>
              <div className="text-xs text-[var(--ml-text-secondary)]">{kernel.patch_level || "—"}{kernel.notes ? ` · ${kernel.notes}` : ""}</div>
            </div>
          )}
          {!os && !kernel && <div className="text-sm text-[var(--ml-text-secondary)]">No OS or kernel inventory yet.</div>}
        </div>
      </div>

      <div className="ml-card p-5 border-l-4 border-[var(--ml-primary)]" data-testid="sap-overview-note">
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1 flex items-center gap-1.5">
          <FileText size={11} weight="bold" /> Anti-conflation note
        </div>
        <p className="text-sm">
          SAP ECC is treated as a portable SAP application <strong>hosted on</strong> IBM i / Db2 for i — not as an
          IBM i ERP. The existing IBM i metadata (DSPFD / DSPFFD / DSPDBR / DSPPGMREF / DSPOBJD) remains the source of
          truth for the platform underneath. SAP-side inventory is purely additive.
        </p>
      </div>
    </div>
  );
}

function ComponentsTab({ components }) {
  if (components.length === 0) {
    return <div className="ml-card p-8 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="sap-components-empty">No components ingested.</div>;
  }
  return (
    <div className="ml-card overflow-hidden" data-testid="sap-components-table">
      <table className="w-full text-sm">
        <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
          <tr>
            <th className="px-4 py-2 text-left">Component</th>
            <th className="px-4 py-2 text-left">Release</th>
            <th className="px-4 py-2 text-left">EhP</th>
            <th className="px-4 py-2 text-left">SP level</th>
            <th className="px-4 py-2 text-right">Patch</th>
            <th className="px-4 py-2 text-left">Description</th>
          </tr>
        </thead>
        <tbody>
          {components.map((c) => (
            <tr key={c.component} data-testid={`sap-comp-row-${c.component}`} className="border-b border-[var(--ml-border)]">
              <td className="px-4 py-2 ml-mono font-semibold">{c.component}</td>
              <td className="px-4 py-2 ml-mono">{c.release || "—"}</td>
              <td className="px-4 py-2 ml-mono">{c.ehp || "—"}</td>
              <td className="px-4 py-2 ml-mono">{c.sp_level || "—"}</td>
              <td className="px-4 py-2 ml-mono text-right">{c.patch_level ?? "—"}</td>
              <td className="px-4 py-2 text-[var(--ml-text-secondary)]">{c.description || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlatformTab({ platform }) {
  if (platform.length === 0) {
    return <div className="ml-card p-8 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="sap-platform-empty">No platform inventory ingested.</div>;
  }
  return (
    <div className="ml-card overflow-hidden" data-testid="sap-platform-table">
      <table className="w-full text-sm">
        <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
          <tr>
            <th className="px-4 py-2 text-left">Kind</th>
            <th className="px-4 py-2 text-left">Name</th>
            <th className="px-4 py-2 text-left">Version</th>
            <th className="px-4 py-2 text-left">Patch / TR</th>
            <th className="px-4 py-2 text-left">Notes</th>
          </tr>
        </thead>
        <tbody>
          {platform.map((p) => (
            <tr key={`${p.kind}-${p.name}`} data-testid={`sap-plat-row-${p.kind}`} className="border-b border-[var(--ml-border)]">
              <td className="px-4 py-2 ml-mono">
                <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest border border-[var(--ml-border)]">{p.kind}</span>
              </td>
              <td className="px-4 py-2 font-semibold">{p.name}</td>
              <td className="px-4 py-2 ml-mono">{p.version || "—"}</td>
              <td className="px-4 py-2 ml-mono">{p.patch_level || "—"}</td>
              <td className="px-4 py-2 text-xs text-[var(--ml-text-secondary)]">{p.notes || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TablesTab({ workspaceId }) {
  const [data, setData] = useState({ total: 0, rows: [] });
  const [loading, setLoading] = useState(true);
  const [linkedOnly, setLinkedOnly] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    listSapTables(workspaceId, { linkedOnly, limit: 500 })
      .then(setData)
      .finally(() => setLoading(false));
  }, [workspaceId, linkedOnly]);

  if (loading) return <div className="ml-card p-8 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="sap-tables-loading"><Spinner size={14} className="animate-spin inline mr-2" />Loading…</div>;
  if (data.rows.length === 0) return <div className="ml-card p-8 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="sap-tables-empty">No SAP tables ingested yet.</div>;

  const rows = filter.trim()
    ? data.rows.filter((r) => r.sap_table.toLowerCase().includes(filter.toLowerCase()) || (r.description || "").toLowerCase().includes(filter.toLowerCase()))
    : data.rows;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Filter by table name or description"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="sap-tables-filter"
          className="flex-1 min-w-[200px] px-3 py-2 text-sm border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)]"
        />
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={linkedOnly}
            onChange={(e) => setLinkedOnly(e.target.checked)}
            data-testid="sap-tables-linked-only"
          />
          Only tables linked to IBM i objects
        </label>
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]" data-testid="sap-tables-total">
          {rows.length} / {data.total} shown
        </div>
      </div>
      <div className="ml-card overflow-hidden" data-testid="sap-tables-table">
        <table className="w-full text-sm">
          <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
            <tr>
              <th className="px-4 py-2 text-left">SAP table</th>
              <th className="px-4 py-2 text-left">Class</th>
              <th className="px-4 py-2 text-left">Size</th>
              <th className="px-4 py-2 text-left">Description</th>
              <th className="px-4 py-2 text-right">Rows</th>
              <th className="px-4 py-2 text-left">IBM i object</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`sap-table-row-${r.sap_table}`} className="border-b border-[var(--ml-border)]">
                <td className="px-4 py-2 ml-mono font-semibold">{r.sap_table}</td>
                <td className="px-4 py-2 ml-mono">{r.delivery_class || "—"}</td>
                <td className="px-4 py-2 ml-mono">{r.ddic_size_cat || "—"}</td>
                <td className="px-4 py-2 text-xs text-[var(--ml-text-secondary)]">{r.description || "—"}</td>
                <td className="px-4 py-2 ml-mono text-right">{r.row_count != null ? r.row_count.toLocaleString() : "—"}</td>
                <td className="px-4 py-2 ml-mono">
                  {r.ibmi_object ? (
                    <span className="inline-flex items-center gap-1 text-[var(--ml-primary)]" title={`${r.ibmi_object.library}/${r.ibmi_object.object_name} · ${r.ibmi_object.object_type}`}>
                      <LinkIcon size={12} weight="bold" />
                      {r.ibmi_object.library}/{r.ibmi_object.object_name}
                    </span>
                  ) : (
                    <span className="text-[var(--ml-text-secondary)]">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ImportTab({ workspaceId, onReload }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lastResult, setLastResult] = useState(null);

  const handleFile = async (e, isZip = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setLastResult(null);
    try {
      const fn = isZip ? importSapBulkZip : importSapExtract;
      const res = await fn(workspaceId, file, (pe) => {
        if (pe.total) setProgress(Math.round((pe.loaded / pe.total) * 100));
      });
      setLastResult({ ...res, isZip });
      if (isZip) {
        const totalIngested = Object.values(res.totals_by_extract || {}).reduce((a, b) => a + b, 0);
        toast.success(`Ingested ${totalIngested} rows across ${Object.keys(res.totals_by_extract || {}).length} extract types.`);
      } else if (res.status === "ingested") {
        toast.success(`Ingested ${res.rows_ingested} rows from ${res.extract_label}.`);
      } else {
        toast.error(`Couldn't recognise this file. ${res.warnings?.[0] || ""}`);
      }
      if (res.source_system_type_flipped) {
        toast.message("Workspace upgraded to SAP ECC on IBM i mode", {
          description: "Existing IBM i features remain active — this just adds the SAP-side layer.",
        });
      }
      await onReload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-5" data-testid="sap-ecc-import">
      <div className="grid md:grid-cols-2 gap-5">
        <div className="ml-card p-5" data-testid="sap-import-single-card">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex items-center gap-1.5">
            <Upload size={11} weight="bold" /> Single extract
          </div>
          <p className="text-sm text-[var(--ml-text-secondary)] mb-3">
            Upload one CSV / Excel / tab-delimited extract. We auto-detect the type from
            filename or header shape (8 MiB max).
          </p>
          <input
            type="file"
            accept=".csv,.xlsx,.xls,.tab,.tsv,.txt"
            onChange={(e) => handleFile(e, false)}
            disabled={uploading}
            data-testid="sap-import-single-input"
            className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[var(--ml-primary)] file:text-white file:font-semibold file:cursor-pointer disabled:opacity-50"
          />
        </div>
        <div className="ml-card p-5" data-testid="sap-import-zip-card">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex items-center gap-1.5">
            <Upload size={11} weight="bold" /> Bulk ZIP
          </div>
          <p className="text-sm text-[var(--ml-text-secondary)] mb-3">
            One ZIP containing components / platform / sap_tables extracts (32 MiB max, 200 files).
          </p>
          <input
            type="file"
            accept=".zip"
            onChange={(e) => handleFile(e, true)}
            disabled={uploading}
            data-testid="sap-import-zip-input"
            className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[var(--ml-primary)] file:text-white file:font-semibold file:cursor-pointer disabled:opacity-50"
          />
        </div>
      </div>

      {uploading && progress > 0 && progress < 100 && (
        <div className="text-sm text-[var(--ml-text-secondary)]" data-testid="sap-import-progress">
          Uploading… {progress}%
        </div>
      )}

      {lastResult && (
        <div className="ml-card p-5" data-testid="sap-import-result">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2">Last import</div>
          {lastResult.isZip ? (
            <>
              <div className="text-sm mb-2">
                Processed <strong>{lastResult.files_processed}</strong> file(s):
              </div>
              <ul className="space-y-1 text-xs ml-mono">
                {lastResult.per_file.map((f) => (
                  <li
                    key={f.file}
                    data-testid={`sap-import-result-${f.file}`}
                    className={f.status === "ingested" ? "text-[#0e6027]" : "text-[var(--ml-text-secondary)]"}
                  >
                    {f.status === "ingested" ? <CheckCircle size={12} weight="bold" className="inline mr-1" /> : <WarningOctagon size={12} weight="bold" className="inline mr-1" />}
                    <strong>{f.file}</strong> — {f.status}
                    {f.extract_type && <> · {f.extract_type} · {f.rows_ingested} rows</>}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="text-sm">
              <strong>{lastResult.filename}</strong> →{" "}
              {lastResult.status === "ingested" ? (
                <span className="text-[#0e6027]">{lastResult.extract_label} · {lastResult.rows_ingested} rows ingested</span>
              ) : (
                <span className="text-[#a2191f]">Unrecognised — {lastResult.warnings?.join("; ")}</span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="ml-card p-5" data-testid="sap-templates-card">
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-3 flex items-center gap-1.5">
          <Download size={11} weight="bold" /> Templates
        </div>
        <p className="text-sm text-[var(--ml-text-secondary)] mb-3">
          Don&apos;t have an extract handy? Start with one of these templates — fill it in with your data and upload above.
          Header synonyms are accepted, so close-enough column names also work.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          {TEMPLATE_KEYS.map((t) => (
            <a
              key={t.key}
              href={sapTemplateUrl(workspaceId, t.key)}
              download={`${t.key}-template.csv`}
              data-testid={`sap-template-${t.key}`}
              className="ml-card p-4 hover:bg-[var(--ml-surface)]/40 transition-colors block"
            >
              <div className="font-semibold mb-1">{t.label}</div>
              <div className="text-xs text-[var(--ml-text-secondary)] mb-3">{t.desc}</div>
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] flex items-center gap-1">
                <Download size={11} weight="bold" /> Download CSV
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
